import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLead } from "../lib/store";
import { WhatsAppIcon } from "./icons";

const LINKS = [
  { to: "https://beyondtravel.co.id", label: "Home" },
  { to: "/destinations", label: "Destinasi" },
  { to: "/land-arrangement", label: "Land Arrangement" },
  { to: "/private-trip", label: "Private Trip" },
  { to: "/corporate-group", label: "Corporate & Group" },
  { to: "/about", label: "Tentang Kami" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openLead } = useLead();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const solid = scrolled || open;

  return (
    <header
      data-testid="nav-main-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${solid ? "glass py-2" : "bg-transparent py-4"
        }`}
    >
      <div className="container-x flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <img src="/logo.svg" alt="Beyond Travel" className="h-10 w-10 rounded-xl" />
          <span className="leading-none">
            <span className={`block font-heading text-base font-extrabold tracking-wide transition-colors ${solid ? "text-charcoal" : "text-white"}`}>
              BEYOND <span className="text-gold">TRAVEL</span>
            </span>
            <span className={`mt-1 block text-[10px] font-medium uppercase tracking-[0.22em] transition-colors ${solid ? "text-slate-500" : "text-white/70"}`}>
              Travel More, Explore More
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-5 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1).replace("/", "-")}`}
              className={({ isActive }) =>
                `relative text-[13px] font-medium tracking-wide transition-colors hover:text-gold ${isActive ? "text-gold" : solid ? "text-charcoal" : "text-white/90"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => openLead()}
            data-testid="nav-cta-consult-button"
            className="btn btn-wa hidden !px-5 !py-2.5 text-[13px] md:inline-flex"
          >
            <WhatsAppIcon className="h-4 w-4" /> WhatsApp Us
          </button>
          <button
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="nav-hamburger-button"
            className={`inline-flex h-11 w-11 items-center justify-center rounded-xl transition lg:hidden ${solid ? "bg-tosca-600 text-white" : "bg-white/15 text-white backdrop-blur"
              }`}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 top-0 -z-10 flex flex-col justify-center bg-tosca-900/95 px-8 backdrop-blur-xl lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-3 font-heading text-2xl font-bold ${isActive ? "text-gold" : "text-white"}`}
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              onClick={() => {
                setOpen(false);
                openLead();
              }}
              className="btn btn-wa mt-8 w-full"
              data-testid="nav-mobile-wa-button"
            >
              <WhatsAppIcon className="h-5 w-5" /> WhatsApp Us
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
